import ExcelJS from "exceljs";

import { downloadFile, getImageBlob } from "./downloadFile";

/**
 * Sets options for a cell in the Excel worksheet.
 *
 * @param {object} cell - The cell object from the ExcelJS worksheet.
 * @param {object} options - An object containing the options to set on the cell.
 *
 * @returns {void}
 */
const setOptions = (cell, options) => {
  Object.keys(options).forEach((key) => {
    cell[key] = options[key];
  });
};

/**
 * Sets the value and options for a cell in the Excel worksheet.
 *
 * @param {Workbook} wb - The ExcelJS workbook object.
 * @param {string} id - The ID of the cell to set.
 * @param {*} value - The value to set for the cell.
 * @param {object} options - An object containing the options to set on the cell.
 * @param {object} logger - A logger object for logging errors.
 *
 * @returns {void}
 */
const setCell = (wb, id, value, options = {}, logger = console) => {
  if (!value) {
    logger.error("No value provided for cell", wb, id);
    return;
  }

  if (id.indexOf(":") > -1) {
    wb.mergeCells(id);
    id = id.split(":")[0];
  }

  const cell = wb.getCell(id);

  cell.value = value;

  setOptions(cell, options);
};

/**
 * Gets the column key based on the column name.
 *
 * @param {string} columnName - The name of the column.
 *
 * @returns {string} - The key corresponding to the column name.
 */
const getColumnKey = (columnName) => {
  switch (columnName) {
    // Script
    case "No":
      return "id";
    case "Paragraph Text":
      return "text";
    case "Messaging":
      return "visualMessaging";
    // Personas
    // Notes
    case "Customer Name":
      return "customer";
    default:
      // transform to camelCase
      return columnName
        .replace(/[^a-zA-Z0-9 ]/g, "") // remove non-alphanumeric except space
        .split(" ")
        .map((word, idx) =>
          idx === 0
            ? word.charAt(0).toLowerCase() + word.slice(1)
            : word.charAt(0).toUpperCase() + word.slice(1),
        )
        .join("");
  }
};

/**
 * Adds images to the specified worksheet in the Excel workbook.
 *
 * @param {Workbook} workbook - The ExcelJS workbook object.
 * @param {Worksheet} worksheet - The ExcelJS worksheet object where images will be added.
 * @param {Array} columnsIds - An array of column IDs to determine where to place the images.
 * @param {Array} items - An array of image URLs to be added to the worksheet.
 * @param {number} width - The width of the images to be added.
 * @param {number} height - The height of the images to be added.
 *
 * @returns {Promise<void>} - A promise that resolves when all images have been added.
 */
const addImagesToSheet = async (
  workbook,
  worksheet,
  columnsIds,
  items,
  width,
  height,
) => {
  const imageIdIndex = columnsIds.findIndex(({ id }) => id === "image");

  if (imageIdIndex > -1) {
    const imagePromises = await Promise.allSettled(
      items.map(
        (url, index) =>
          new Promise(async (resolve, reject) => {
            if (url) {
              try {
                const { image, blob } = await getImageBlob({
                  url,
                });
                const buffer = await blob.arrayBuffer();
                const imageRef = workbook.addImage({
                  buffer,
                  extension: image.format,
                });

                return resolve({ imageRef, index });
              } catch (error) {
                console.error(
                  "Error downloading image for script item:",
                  error,
                );
                return reject(null);
              }
            }

            reject(null);
          }),
      ),
    );

    imagePromises
      .filter(({ status }) => status === "fulfilled")
      .forEach(({ value: { imageRef, index } }) => {
        worksheet.addImage(imageRef, {
          tl: { col: imageIdIndex, row: index + 1 },
          ext: { width, height },
        });
      });
  }
};

/**
 * Generates an Excel file from the provided parameters.
 * @param {object} params - The parameters for generating the Excel file.
 *
 * @returns {Promise<Buffer>} - A promise that resolves to the Excel file buffer.
 */
const getExcel = async (params) => {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = params.user.name;
  workbook.created = new Date();

  workbook.views = [
    {
      x: 0,
      y: 0,
      width: 1000,
      height: 1000,
      firstSheet: 0,
      activeTab: 1,
      visibility: "visible",
    },
  ];

  const scriptSheet = workbook.addWorksheet("Script");
  const personasSheet = workbook.addWorksheet("Personas");
  const notesSheet = workbook.addWorksheet("Notes");

  const scriptColumnsIds = params.story.script.columns.map(({ id, name }) => ({
    id,
    name,
  }));
  const personasColumnsIds = params.story.personas.columns.map(
    ({ id, name }) => ({ id, name }),
  );

  if (params.story.script.items.some((item) => !!item.visualUrl)) {
    scriptColumnsIds.push(
      { id: "visualPrompt", name: "Visual Prompt" },
      { id: "image", name: "Image" },
    );
  }

  if (params.story.personas.items.some((item) => !!item.avatarUrl)) {
    personasColumnsIds.push(
      { id: "descriptionPrompt", name: "Description Prompt" },
      { id: "avatarPrompt", name: "Avatar Prompt" },
      { id: "image", name: "Image" },
    );
  }

  scriptSheet.addTable({
    name: "Script",
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: scriptColumnsIds.map(({ name }) => ({
      name,
      filterButton: true,
    })),
    rows: params.story.script.items.map((item) => {
      const ret = { ...item };

      Object.keys(item).forEach((key) => {
        if (key === "image") {
          ret[key] = "";
        } else if (key === "personas") {
          ret[key] = item[key]
            .map((idx) => params.story.personas.items[idx]?.name ?? idx)
            .join("\n");
        } else if (Array.isArray(item[key])) {
          ret[key] = item[key].join("\n");
        }
      });

      return scriptColumnsIds.map(({ id }) => ret[id]);
    }),
  });

  [8, 30, 65, 50, 50, 25, 28, 28, 34, 34, 50, 30].map((width, index) => {
    if (width) {
      setOptions(scriptSheet.getColumn(index + 1), {
        width,
        alignment: { wrapText: true },
      });
    }
  });

  await addImagesToSheet(
    workbook,
    scriptSheet,
    scriptColumnsIds,
    params.story.script.items.map(({ visualUrl }) => visualUrl),
    180,
    101,
  );

  personasSheet.addTable({
    name: "Personas",
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: personasColumnsIds.map(({ name }) => ({
      name,
      filterButton: true,
    })),
    rows: params.story.personas.items.map((item) => {
      const ret = { ...item };

      Object.keys(item).forEach((key) => {
        if (key === "image") {
          ret[key] = "";
        } else if (Array.isArray(item[key])) {
          ret[key] = item[key].join("\n");
        }
      });

      return personasColumnsIds.map(({ id }) => ret[id]);
    }),
  });

  [20, 30, 30, 60, 20, 8, 50, 50, 30].map((width, index) => {
    if (width) {
      setOptions(personasSheet.getColumn(index + 1), {
        width,
        alignment: { wrapText: true },
      });
    }
  });

  await addImagesToSheet(
    workbook,
    personasSheet,
    personasColumnsIds,
    params.story.personas.items.map(({ avatarUrl }) => avatarUrl),
    101,
    101,
  );

  const storySections = [
    ...new Set(params.story.script.items.map(({ section }) => section)),
  ];

  notesSheet.addTable({
    name: "Notes",
    ref: "A1",
    // headerRow: false,
    style: {
      theme: "TableStyleMedium2",
      showFirstColumn: true,
      showColumnStripes: true,
    },
    columns: [{ name: "Key" }, { name: "Value" }],
    rows: [
      ["Title", params.story.title || ""],
      ["Customer Name", params.story.customer || ""],
      ["Industry", params.story.industry || ""],
      ["Campaign Focus", params.story.campaignFocus || ""],
      [
        "Story Overview",
        `${params.story.storyOverview || ""}${!!storySections.length ? `\n\n${storySections.join("\n")}` : ""}` ||
          "",
      ],
      ["Business Goals", params.story.businessGoals || ""],
      ["Products", params.story?.products?.join("\n") || ""],
    ],
  });

  setOptions(notesSheet.getColumn(1), {
    width: 30,
    alignment: { wrapText: true },
  });

  setOptions(notesSheet.getColumn(2), {
    width: 80,
    alignment: { wrapText: true },
  });

  return workbook;
};

/**
 * Downloads the story data as an Excel file.
 *
 * @param {object} params - The parameters for downloading the Excel file.
 *
 * @returns {Promise<void>} - A promise that resolves when the file is downloaded.
 * @throws {Error} If the parameters are invalid or if an error occurs during file generation
 */
const downloadStoryExcel = async (params) => {
  if (!params || !params.story) {
    throw new Error("Invalid parameters provided for downloading Excel file.");
  }

  try {
    const workbook = await getExcel(params);
    const buffer = await workbook.xlsx.writeBuffer();

    downloadFile(
      buffer,
      `storyteller-ai-${params.story.customer}-${params.story.industry}-${Date.now()}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  } catch (error) {
    console.error("Error generating Excel file:", error);
    throw error;
  }
};

/**
 * Parses an Excel file and extracts story data.
 *
 * @param {File} file - The Excel file to parse.
 *
 * @returns {Promise<object>} - A promise that resolves to an object containing the parsed story data.
 * @throws {Error} If the file is not provided or if an error occurs during parsing
 */
const parseTemplateExcel = async (file) => {
  if (!file) {
    throw new Error("No file provided for parsing.");
  }

  const story = {
    title: "",
    storyOverview: "",
    customer: "",
    industry: "",
    businessGoals: "",
    campaignFocus: "",
    personas: [],
    products: [],
    script: [],
  };

  try {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(file.arrayBuffer());

    const sheets = workbook.worksheets
      .filter((ws) => ["script", "personas"].includes(ws.name.toLowerCase()))
      .filter(Boolean);

    sheets.forEach((sheet, idx) => {
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) {
          return;
        }
        const rowData = {};
        row.eachCell((cell, colNumber) => {
          if (!cell.value) {
            return;
          }

          const key = getColumnKey(sheet.getRow(1).getCell(colNumber).value);

          if (["script", "personas"].includes(key)) {
            rowData[key] = cell.value.split(/[,\n]/).map((item) => item.trim());
          } else {
            rowData[key] = cell.value;
          }
        });

        rowData.id = rowNumber - 2; // Adjusting index to start from 0

        if (idx === 0) {
          story.script.push(rowData);
        } else {
          story.personas.push(rowData);
        }
      });
    });

    workbook.worksheets
      .find((ws) => ws.name.toLowerCase() === "notes")
      ?.eachRow((row, rowNumber) => {
        const key = getColumnKey(row.getCell(1).value);
        const value = row.getCell(2).value;
        if (key && value) {
          if (key === "products") {
            story[key] = value.split(/[,\n]/).map((item) => item.trim());
          } else {
            story[key] = value;
          }
        }
      });

    story.script.forEach((step) => {
      if (step.personas?.length) {
        step.personas = step.personas.map((name) =>
          story.personas.findIndex((persona) => persona.name.includes(name)),
        );
      }
    });

    return story;

    // logger.debug("Parsed Excel file", JSON.stringify(story, null, 2));
    // return json;
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    throw new Error("Error parsing Excel file: " + error.message);
  }
};

export { downloadStoryExcel, parseTemplateExcel };
